import { State, ActionType } from './types';
import { sendChatbotOpeningSignal } from 'src/api';
import { getDataFromSessionStorage, KEYS } from 'src/utils/persistentStorage';
import analytics from 'src/utils/analytics/analytics';
import { TrackPageViewedData } from 'src/utils/analytics/types';
import { shouldWidgetCollectPersonalData } from 'src/utils/cookieBanners';

const routerHistory = getDataFromSessionStorage(KEYS.routerHistory);
const widgetWasOpened =
  getDataFromSessionStorage(KEYS.widgetIsOpen) !== null ? getDataFromSessionStorage(KEYS.widgetIsOpen) : false;
const isChatbotOpeningSignalSent =
  getDataFromSessionStorage(KEYS.isChatbotOpeningSignalSent) !== null
    ? getDataFromSessionStorage(KEYS.isChatbotOpeningSignalSent)
    : false;

export const initialState = {
  widgetData: undefined,
  widgetIsOpen: widgetWasOpened,
  previousMessageId: undefined,
  currentMessageId: undefined,
  currentMessageTitle: undefined,
  currentMessage: undefined,
  currentPage: undefined,
  pages: undefined,
  responsesCurrentBlobInfo: {
    index: 0,
    itemsWithin: undefined,
  },
  isResponsesGroupHide: false,
  routerHistory: routerHistory !== null ? routerHistory : [],
  assets: {},
  isFirstClickRegistered: isChatbotOpeningSignalSent,
  lottieReact: undefined,
};

export function appContextReducer(state: State, action: ActionType) {
  switch (action.type) {
    case 'setWidgetInitialData': {
      return {
        ...state,
        widgetData: action.payload.widgetData,
        widgetIsOpen: action.payload.widgetIsOpen,
        currentMessageId: action.payload.currentMessageId,
        routerHistory: action.payload.routerHistory ? action.payload.routerHistory : [action.payload.currentMessageId],
        responsesCurrentBlobInfo: {
          index: 0,
          itemsWithin: action.payload.responsesBlobItemsWithin,
        },
        currentMessage: action.payload.currentMessage,
        currentPage: action.payload.currentPage,
        pages: action.payload.pages,
        isResponsesGroupHide: action.payload.isResponsesGroupHide,
      };
    }
    case 'makeWidgetReset': {
      const currentMessage = state.widgetData?.config.script[state.widgetData?.config.firstMessageId];

      return {
        ...state,
        currentMessageId: state.widgetData?.config.firstMessageId,
        currentMessage: currentMessage,
        currentPage: currentMessage?.pages[0],
        pages: currentMessage?.pages,
        routerHistory: [state.widgetData?.config.firstMessageId],
        responsesCurrentBlobInfo: {
          index: 0,
          itemsWithin: undefined,
        },
        isResponsesGroupHide: false,
      };
    }
    case 'setAssets':
      return {
        ...state,
        assets: action.assets,
      };
    case 'setWidgetOpenedStatus':
      if (state.currentMessageId) {
        if (state.currentMessage) {
          analytics.registerMessageData({ messageId: state.currentMessageId });

          analytics.trackMessageViewed({
            previousMessageId: state.currentMessageId,
            messageLabel: state.currentMessage.label,
          });
        }

        if (state.currentPage) {
          const trackedData: TrackPageViewedData = {
            messageId: state.currentMessageId,
            previousMessageId: state.currentMessageId,
            pageId: state.currentPage.id,
            pageType: state.currentPage.pageType,
            pageCaption: state.currentPage.caption,
          };

          if ('asset' in state.currentPage && state.currentPage.asset) {
            trackedData.assetType = state.currentPage.asset.type;
          }

          analytics.trackPageViewed(trackedData);
        }
      }

      return {
        ...state,
        widgetIsOpen: action.statusFlag,
      };
    case 'makeNavigationMove': {
      if (state.currentMessageId && action.payload.currentMessage) {
        analytics.registerMessageData({ messageId: action.payload.currentMessageId });

        analytics.trackMessageViewed({
          previousMessageId: state.currentMessageId,
          messageLabel: action.payload.currentMessage.label,
        });
      }

      if (action.payload.currentPage) {
        const trackedData: TrackPageViewedData = {
          messageId: action.payload.currentMessageId,
          previousMessageId: state.currentMessageId,
          pageId: action.payload.currentPage.id,
          pageType: action.payload.currentPage.pageType,
          pageCaption: action.payload.currentPage.caption,
        };

        if ('asset' in action.payload.currentPage && action.payload.currentPage.asset) {
          trackedData.assetType = action.payload.currentPage.asset.type;
        }

        analytics.trackPageViewed(trackedData);
      }

      return {
        ...state,
        previousMessageId: state.currentMessageId,
        currentMessageId: action.payload.currentMessageId,
        currentMessage: action.payload.currentMessage,
        currentPage: action.payload.currentPage,
        pages: action.payload.pages,
        routerHistory: action.payload.routerHistory,
        responsesCurrentBlobInfo: {
          index: 0,
          itemsWithin: action.payload.responsesBlobItemsWithin,
        },
      };
    }
    case 'setResponsesCurrentBlobInfo':
      return {
        ...state,
        responsesCurrentBlobInfo: action.currentBlobInfo,
      };

    case 'toggleResponsesGroupVisibility':
      if (action.payload.isResponsesGroupHide) {
        return {
          ...state,
          isResponsesGroupHide: action.payload.isResponsesGroupHide,
        };
      }
      return {
        ...state,
        isResponsesGroupHide: action.payload.isResponsesGroupHide,
        currentPage: undefined,
      };

    case 'selectPage': {
      if (state.currentMessageId) {
        const trackedData: TrackPageViewedData = {
          pageId: action.payload.currentPage.id,
          pageType: action.payload.currentPage.pageType,
          pageCaption: action.payload.currentPage.caption,
          messageId: state.currentMessageId,
          previousMessageId: state.previousMessageId,
        };

        if ('asset' in action.payload.currentPage && action.payload.currentPage.asset) {
          trackedData.assetType = action.payload.currentPage.asset.type;
        }

        analytics.trackPageViewed(trackedData);

        return {
          ...state,
          currentPage: action.payload.currentPage,
        };
      }
      return state;
    }

    case 'registerFirstClick': {
      const hasWidgetEverOpened = getDataFromSessionStorage(KEYS.widgetIsOpen) == null;
      const isFirstClickRegistered = getDataFromSessionStorage(KEYS.isChatbotOpeningSignalSent);

      if (
        state.widgetData?.parameters.isAutoopenEnabled &&
        hasWidgetEverOpened &&
        !isFirstClickRegistered &&
        state.routerHistory.length === 1
      ) {
        if (shouldWidgetCollectPersonalData()) {
          sendChatbotOpeningSignal({
            onError: err => console.error(err),
          });
        }

        if (state.currentMessageId) {
          if (state.currentMessage) {
            analytics.registerMessageData({ messageId: state.currentMessageId });

            analytics.trackMessageViewed({
              previousMessageId: state.currentMessageId,
              messageLabel: state.currentMessage.label,
            });
          }

          if (state.currentPage) {
            const trackedData: TrackPageViewedData = {
              messageId: state.currentMessageId,
              previousMessageId: state.currentMessageId,
              pageId: state.currentPage.id,
              pageType: state.currentPage.pageType,
              pageCaption: state.currentPage.caption,
            };

            if ('asset' in state.currentPage && state.currentPage.asset) {
              trackedData.assetType = state.currentPage.asset.type;
            }

            analytics.trackPageViewed(trackedData);
          }
        }

        return { ...state, isFirstClickRegistered: true };
      }
      return state;
    }

    case 'setLottieReact':
      return {
        ...state,
        lottieReact: action.payload.lottieReact,
      };
  }
}
